import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const IN = 0.0254;

// Desk portion (matches dormDeskHutchRenderer.js)
const DESK_H = 30 * IN;
const TOP_THICK = 1 * IN;
const PANEL_THICK = 0.75 * IN;
const DRAWER_GAP = 0.4 * IN;
const PEDESTAL_W = 14.5 * IN;
const NUM_DRAWERS = 3;
const PULL_W = 5 * IN;
const ARC_RISE = 1 * IN;

// Hutch portion
const HUTCH_DEPTH = 12 * IN;
const HUTCH_PANEL_THICK = 0.75 * IN;
const SHELF_THICK = 0.75 * IN;
const RAIL_FROM_TOP = 2.5 * IN;

export function DormDeskHutchSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        drawFrame={false}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <HutchDeskSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT — y=0 is top of hutch, y=height is floor
  const deskSurfaceY = height - DESK_H;
  const deskBodyTop = deskSurfaceY + TOP_THICK;
  const deskBodyH = height - deskBodyTop;

  // Desk drawers
  const drawerH = (deskBodyH - (NUM_DRAWERS - 1) * DRAWER_GAP) / NUM_DRAWERS;
  const dividerX = width - PANEL_THICK - PEDESTAL_W;
  const pedestalCenterX = dividerX + PEDESTAL_W / 2;

  // Hutch
  const hutchShelfY = deskSurfaceY / 2;
  const railY = SHELF_THICK + RAIL_FROM_TOP;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* ── Hutch ── */}
      <Line
        x1={HUTCH_PANEL_THICK}
        y1={0}
        x2={HUTCH_PANEL_THICK}
        y2={deskSurfaceY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={width - HUTCH_PANEL_THICK}
        y1={0}
        x2={width - HUTCH_PANEL_THICK}
        y2={deskSurfaceY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={HUTCH_PANEL_THICK}
        y1={hutchShelfY}
        x2={width - HUTCH_PANEL_THICK}
        y2={hutchShelfY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={HUTCH_PANEL_THICK}
        y1={railY}
        x2={width - HUTCH_PANEL_THICK}
        y2={railY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* ── Desk ── */}
      <Line
        x1={0}
        y1={deskSurfaceY}
        x2={width}
        y2={deskSurfaceY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={0}
        y1={deskBodyTop}
        x2={width}
        y2={deskBodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={PANEL_THICK}
        y1={deskBodyTop}
        x2={PANEL_THICK}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={dividerX}
        y1={deskBodyTop}
        x2={dividerX}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {Array.from({ length: NUM_DRAWERS - 1 }, (_, g) => {
        const y =
          deskBodyTop + (g + 1) * drawerH + g * DRAWER_GAP + DRAWER_GAP / 2;
        return (
          <Line
            key={`gap-${g}`}
            x1={dividerX}
            y1={y}
            x2={width}
            y2={y}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
      {Array.from({ length: NUM_DRAWERS }, (_, d) => {
        const cy = deskBodyTop + d * (drawerH + DRAWER_GAP) + drawerH / 2;
        const x1 = pedestalCenterX - PULL_W / 2;
        const x2 = pedestalCenterX + PULL_W / 2;
        return (
          <path
            key={`pull-${d}`}
            d={`M ${x1} ${cy} Q ${pedestalCenterX} ${cy - ARC_RISE} ${x2} ${cy}`}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </SchematicShell>
  );
}

function HutchDeskSide({ depth, height, strokeColor, strokeWidth }) {
  const deskSurfaceY = height - DESK_H;
  const deskBodyTop = deskSurfaceY + TOP_THICK;
  const hutchShelfY = deskSurfaceY / 2;
  const hutchFrontX = depth - HUTCH_DEPTH;

  return (
    <g>
      {/* L-shaped outline: hutch at back (right), desk full depth */}
      <path
        d={`M ${hutchFrontX} 0 L ${depth} 0 L ${depth} ${height} L 0 ${height} L 0 ${deskSurfaceY} L ${hutchFrontX} ${deskSurfaceY} Z`}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Desk top band */}
      <Line
        x1={0}
        y1={deskBodyTop}
        x2={depth}
        y2={deskBodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Hutch shelf */}
      <Line
        x1={hutchFrontX}
        y1={hutchShelfY}
        x2={depth}
        y2={hutchShelfY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

DormDeskHutchSchematic.supportsSideViews = true;
